import { useEffect, useRef, type ComponentRef } from 'react';
import { useThree } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import * as THREE from 'three';
import { type Memory } from '../data/locations';
import { GLOBE_RADIUS, HOME_VIEW, latLngToVec3 } from './geo';

type Ref = ComponentRef<typeof CameraControls>;

const FOV = 39; // must match the Canvas camera
const FIT_FRAC = 0.82; // globe fills this fraction of the smaller pane dimension
const MAX_DISTANCE = 12.0; // headroom for framing the globe in a narrow portrait rail
const SPEED_REF = 5.5; // ~overview distance — rotation reaches full speed by here
const IDLE_AFTER_MS = 6000;
const IDLE_SPEED = 0.016; // ~0.9°/s — barely-there drift

const _dir = new THREE.Vector3();
const HOME_DIR = HOME_VIEW.clone().normalize();

/** Distance at which a sphere of GLOBE_RADIUS fills FIT_FRAC of the pane. The
 *  pane can be wide (overview) or a narrow portrait rail (city selected); we fit
 *  to whichever dimension is the tighter constraint so the globe never clips. */
function fitDistance(aspect: number): number {
  const tanHalfV = Math.tan(THREE.MathUtils.degToRad(FOV) / 2);
  const d = (tanHalf: number) => GLOBE_RADIUS * Math.sqrt(1 + 1 / (FIT_FRAC * tanHalf) ** 2);
  return Math.max(d(tanHalfV * aspect), d(tanHalfV));
}

// How close you may zoom. Deeper once the hi-res texture has streamed in; the
// 2K preview stays conservative so the limit never reveals a blurry sphere.
const MIN_HI_DESKTOP = 1.9; // 8K loaded
const MIN_HI_MOBILE = 2.15; // 4K loaded
const MIN_PREVIEW_DESKTOP = 2.35;
const MIN_PREVIEW_MOBILE = 2.55;

/** The ONLY camera authority. Manual interaction + programmatic fly-to both go
 *  through CameraControls; idle auto-rotate uses controls.rotate (which
 *  invalidates), so it works under frameloop="demand". */
export function CameraRig({
  selectedCity,
  reducedMotion,
  isCoarse,
  hiResReady,
}: {
  selectedCity: Memory | null;
  reducedMotion: boolean;
  isCoarse: boolean;
  hiResReady: boolean;
}) {
  const controls = useRef<Ref>(null);
  const gl = useThree((s) => s.gl);
  const size = useThree((s) => s.size); // globe pane size — re-frame when it changes

  const selectedRef = useRef<Memory | null>(selectedCity);
  const activityRef = useRef(0);
  selectedRef.current = selectedCity;

  const minDistance = hiResReady
    ? isCoarse
      ? MIN_HI_MOBILE
      : MIN_HI_DESKTOP
    : isCoarse
      ? MIN_PREVIEW_MOBILE
      : MIN_PREVIEW_DESKTOP;

  // Fly to the selected city (or the overview), framing the globe to fit whatever
  // shape the globe pane currently is — a wide overview, or a narrow portrait rail
  // when a city is selected on desktop. Re-runs on resize (incl. the grid change
  // on selection) so the globe is never clipped.
  useEffect(() => {
    const c = controls.current;
    if (!c) return;
    const aspect = size.width / Math.max(1, size.height);
    const dist = THREE.MathUtils.clamp(fitDistance(aspect), minDistance + 0.3, MAX_DISTANCE);
    if (selectedCity) {
      _dir.copy(latLngToVec3(selectedCity.lat, selectedCity.lng, 1)).normalize();
    } else {
      _dir.copy(HOME_DIR);
    }
    _dir.multiplyScalar(dist);
    void c.setLookAt(_dir.x, _dir.y, _dir.z, 0, 0, 0, !reducedMotion);
  }, [selectedCity, reducedMotion, size, minDistance]);

  // Distance-adaptive feel: rotation and smoothing tighten as you zoom in so the
  // globe never feels twitchy up close. Property writes only — no camera moves —
  // so this never invalidates or fights the demand render loop.
  useEffect(() => {
    const c = controls.current;
    if (!c) return;
    c.dollyToCursor = true;

    const onUpdate = () => {
      const min = c.minDistance;
      const t = THREE.MathUtils.clamp((c.distance - min) / (SPEED_REF - min), 0, 1);
      const speed = THREE.MathUtils.lerp(0.12, 0.55, t); // slow near, free far
      c.azimuthRotateSpeed = speed;
      c.polarRotateSpeed = speed;
      if (!reducedMotion) {
        c.smoothTime = THREE.MathUtils.lerp(0.2, 0.42, t);
        c.draggingSmoothTime = THREE.MathUtils.lerp(0.055, 0.12, t);
      }
    };

    onUpdate();
    c.addEventListener('update', onUpdate);
    return () => c.removeEventListener('update', onUpdate);
  }, [reducedMotion]);

  // Track user activity on the canvas to pause idle rotation.
  useEffect(() => {
    const el = gl.domElement;
    const mark = () => {
      activityRef.current = performance.now();
    };
    el.addEventListener('pointerdown', mark);
    el.addEventListener('wheel', mark, { passive: true });
    el.addEventListener('touchstart', mark, { passive: true });
    return () => {
      el.removeEventListener('pointerdown', mark);
      el.removeEventListener('wheel', mark);
      el.removeEventListener('touchstart', mark);
    };
  }, [gl]);

  // Idle rotation loop (rAF, not useFrame, so it drives demand renders itself).
  useEffect(() => {
    if (reducedMotion) return;
    let raf = 0;
    let last = performance.now();
    const loop = (t: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;
      const idle =
        !selectedRef.current &&
        !document.hidden &&
        performance.now() - activityRef.current > IDLE_AFTER_MS;
      if (idle) controls.current?.rotate(IDLE_SPEED * dt, 0, false);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion]);

  return (
    <CameraControls
      ref={controls}
      makeDefault
      minDistance={minDistance}
      maxDistance={MAX_DISTANCE}
      smoothTime={reducedMotion ? 0.01 : 0.42}
      draggingSmoothTime={reducedMotion ? 0.01 : 0.12}
      truckSpeed={0}
      dollySpeed={0.5}
      azimuthRotateSpeed={0.55}
      polarRotateSpeed={0.55}
    />
  );
}
